package calculator

enum class ScenarioId {
    NO_E_VISA,
    E_VISA,
    APEC,
}

data class CalculatorInput(
    val monthlyIncomeRub: Double = 350_000.0,
    val workDaysPerMonth: Double = 22.0,
    val vndPerThousandRub: Double = 320_000.0,
    val apecCardCostRub: Double = 75_000.0,
    val annualInflationPercent: Double = 10.0,
    val incomeGrowthPercent: Double = 0.0,
    val exchangeRateChangePercent: Double = 0.0,
    val years: Double = 5.0,
    val daysPerYear: Double = 365.0,
    val taxAndOpportunityCostFactor: Double = 1.0,
    val bufferDaysBeforeExit: Double = 0.0,
    val noEVisaRunCostVnd: Double = 1_150_000.0,
    val eVisaRunCostVnd: Double = 3_000_000.0,
    val apecRunCostVnd: Double = 1_150_000.0,
    val noEVisaLodgingCostVndPerRun: Double = 0.0,
    val eVisaLodgingCostVndPerRun: Double = 0.0,
    val apecLodgingCostVndPerRun: Double = 0.0,
    val noEVisaPeriodDays: Double = 45.0,
    val noEVisaStampChangeAfterYears: Double = 2.0,
    val noEVisaChangedPeriodDays: Double = 30.0,
    val eVisaPeriodDays: Double = 90.0,
    val apecPeriodDays: Double = 60.0,
    val noEVisaLostWorkDaysPerRun: Double = 1.0,
    val eVisaLostWorkDaysPerRun: Double = 1.0,
    val apecLostWorkDaysPerRun: Double = 1.0,
    val noEVisaExtraRiskDaysPerRun: Double = 0.0,
    val eVisaExtraRiskDaysPerRun: Double = 0.0,
    val apecExtraRiskDaysPerRun: Double = 0.0,
    val noEVisaAdminHoursPerRun: Double = 0.0,
    val eVisaAdminHoursPerRun: Double = 0.0,
    val apecAdminHoursPerRun: Double = 0.0,
    val noEVisaDiscomfortRubPerRun: Double = 0.0,
    val eVisaDiscomfortRubPerRun: Double = 0.0,
    val apecDiscomfortRubPerRun: Double = 0.0,
    val noEVisaRiskProbabilityPercent: Double = 0.0,
    val eVisaRiskProbabilityPercent: Double = 0.0,
    val apecRiskProbabilityPercent: Double = 0.0,
    val noEVisaRiskEventCostRub: Double = 0.0,
    val eVisaRiskEventCostRub: Double = 0.0,
    val apecRiskEventCostRub: Double = 0.0,
    val fastTrackCrossingsPerYear: Double = 0.0,
    val fastTrackMinutesSavedPerCrossing: Double = 0.0,
    val apecOtherTravelSavingsRub: Double = 0.0,
    val workHoursPerDay: Double = 8.0,
)

data class ScenarioResult(
    val id: ScenarioId,
    val title: String,
    val periodDays: Double,
    val runCostVnd: Double,
    val lostWorkDaysPerRun: Double,
    val adminHoursPerRun: Double,
    val lodgingCostRub: Double,
    val adminTimeCostRub: Double,
    val extraRiskDaysCostRub: Double,
    val expectedRiskCostRub: Double,
    val discomfortCostRub: Double,
    val fastTrackSavingsRub: Double,
    val otherTravelSavingsRub: Double,
    val cardCostRub: Double,
    val runsPerYear: Double,
    val runsForPeriod: Double,
    val travelCostRub: Double,
    val lostWorkDays: Double,
    val incomeLossRub: Double,
    val totalRub: Double,
)

data class ComparisonResult(
    val input: CalculatorInput,
    val scenarios: List<ScenarioResult>,
    val ranking: List<ScenarioResult>,
    val apecBreakEvenEVisaAdminHours: Double,
)

private data class ScenarioConfig(
    val id: ScenarioId,
    val title: String,
    val periodDays: Double,
    val changedPeriodDays: Double,
    val changeAfterYears: Double,
    val runCostVnd: Double,
    val lodgingCostVnd: Double,
    val lostWorkDaysPerRun: Double,
    val extraRiskDaysPerRun: Double,
    val adminHoursPerRun: Double,
    val discomfortRubPerRun: Double,
    val riskProbabilityPercent: Double,
    val riskEventCostRub: Double,
    val fastTrackSavingsRub: Double,
    val otherTravelSavingsRub: Double,
    val cardCostRub: Double,
)

private fun CalculatorInput.scenarioConfigs(): List<ScenarioConfig> {
    return listOf(
        ScenarioConfig(
            id = ScenarioId.NO_E_VISA,
            title = "Штамп",
            periodDays = noEVisaPeriodDays,
            changedPeriodDays = noEVisaChangedPeriodDays,
            changeAfterYears = noEVisaStampChangeAfterYears,
            runCostVnd = noEVisaRunCostVnd,
            lodgingCostVnd = noEVisaLodgingCostVndPerRun,
            lostWorkDaysPerRun = noEVisaLostWorkDaysPerRun,
            extraRiskDaysPerRun = noEVisaExtraRiskDaysPerRun,
            adminHoursPerRun = noEVisaAdminHoursPerRun,
            discomfortRubPerRun = noEVisaDiscomfortRubPerRun,
            riskProbabilityPercent = noEVisaRiskProbabilityPercent,
            riskEventCostRub = noEVisaRiskEventCostRub,
            fastTrackSavingsRub = 0.0,
            otherTravelSavingsRub = 0.0,
            cardCostRub = 0.0,
        ),
        ScenarioConfig(
            id = ScenarioId.E_VISA,
            title = "e-Visa",
            periodDays = eVisaPeriodDays,
            changedPeriodDays = eVisaPeriodDays,
            changeAfterYears = years,
            runCostVnd = eVisaRunCostVnd,
            lodgingCostVnd = eVisaLodgingCostVndPerRun,
            lostWorkDaysPerRun = eVisaLostWorkDaysPerRun,
            extraRiskDaysPerRun = eVisaExtraRiskDaysPerRun,
            adminHoursPerRun = eVisaAdminHoursPerRun,
            discomfortRubPerRun = eVisaDiscomfortRubPerRun,
            riskProbabilityPercent = eVisaRiskProbabilityPercent,
            riskEventCostRub = eVisaRiskEventCostRub,
            fastTrackSavingsRub = 0.0,
            otherTravelSavingsRub = 0.0,
            cardCostRub = 0.0,
        ),
        ScenarioConfig(
            id = ScenarioId.APEC,
            title = "АТЭС",
            periodDays = apecPeriodDays,
            changedPeriodDays = apecPeriodDays,
            changeAfterYears = years,
            runCostVnd = apecRunCostVnd,
            lodgingCostVnd = apecLodgingCostVndPerRun,
            lostWorkDaysPerRun = apecLostWorkDaysPerRun,
            extraRiskDaysPerRun = apecExtraRiskDaysPerRun,
            adminHoursPerRun = apecAdminHoursPerRun,
            discomfortRubPerRun = apecDiscomfortRubPerRun,
            riskProbabilityPercent = apecRiskProbabilityPercent,
            riskEventCostRub = apecRiskEventCostRub,
            fastTrackSavingsRub = fastTrackSavings(this),
            otherTravelSavingsRub = apecOtherTravelSavingsRub,
            cardCostRub = apecCardCostRub,
        ),
    )
}

private fun CalculatorInput.validateForCalculation() {
    val errors = mutableListOf<String>()

    fun requireFinite(name: String, value: Double) {
        if (value.isNaN() || value.isInfinite()) errors += "$name must be finite"
    }

    fun requirePositive(name: String, value: Double) {
        if (value <= 0.0) errors += "$name must be greater than 0"
    }

    fun requireNonNegative(name: String, value: Double) {
        if (value < 0.0) errors += "$name must be 0 or greater"
    }

    val numericInputs = listOf(
        "monthlyIncomeRub" to monthlyIncomeRub,
        "workDaysPerMonth" to workDaysPerMonth,
        "vndPerThousandRub" to vndPerThousandRub,
        "apecCardCostRub" to apecCardCostRub,
        "annualInflationPercent" to annualInflationPercent,
        "incomeGrowthPercent" to incomeGrowthPercent,
        "exchangeRateChangePercent" to exchangeRateChangePercent,
        "years" to years,
        "daysPerYear" to daysPerYear,
        "taxAndOpportunityCostFactor" to taxAndOpportunityCostFactor,
        "bufferDaysBeforeExit" to bufferDaysBeforeExit,
        "noEVisaRunCostVnd" to noEVisaRunCostVnd,
        "eVisaRunCostVnd" to eVisaRunCostVnd,
        "apecRunCostVnd" to apecRunCostVnd,
        "noEVisaLodgingCostVndPerRun" to noEVisaLodgingCostVndPerRun,
        "eVisaLodgingCostVndPerRun" to eVisaLodgingCostVndPerRun,
        "apecLodgingCostVndPerRun" to apecLodgingCostVndPerRun,
        "noEVisaPeriodDays" to noEVisaPeriodDays,
        "noEVisaStampChangeAfterYears" to noEVisaStampChangeAfterYears,
        "noEVisaChangedPeriodDays" to noEVisaChangedPeriodDays,
        "eVisaPeriodDays" to eVisaPeriodDays,
        "apecPeriodDays" to apecPeriodDays,
        "noEVisaLostWorkDaysPerRun" to noEVisaLostWorkDaysPerRun,
        "eVisaLostWorkDaysPerRun" to eVisaLostWorkDaysPerRun,
        "apecLostWorkDaysPerRun" to apecLostWorkDaysPerRun,
        "noEVisaExtraRiskDaysPerRun" to noEVisaExtraRiskDaysPerRun,
        "eVisaExtraRiskDaysPerRun" to eVisaExtraRiskDaysPerRun,
        "apecExtraRiskDaysPerRun" to apecExtraRiskDaysPerRun,
        "noEVisaAdminHoursPerRun" to noEVisaAdminHoursPerRun,
        "eVisaAdminHoursPerRun" to eVisaAdminHoursPerRun,
        "apecAdminHoursPerRun" to apecAdminHoursPerRun,
        "noEVisaDiscomfortRubPerRun" to noEVisaDiscomfortRubPerRun,
        "eVisaDiscomfortRubPerRun" to eVisaDiscomfortRubPerRun,
        "apecDiscomfortRubPerRun" to apecDiscomfortRubPerRun,
        "noEVisaRiskProbabilityPercent" to noEVisaRiskProbabilityPercent,
        "eVisaRiskProbabilityPercent" to eVisaRiskProbabilityPercent,
        "apecRiskProbabilityPercent" to apecRiskProbabilityPercent,
        "noEVisaRiskEventCostRub" to noEVisaRiskEventCostRub,
        "eVisaRiskEventCostRub" to eVisaRiskEventCostRub,
        "apecRiskEventCostRub" to apecRiskEventCostRub,
        "fastTrackCrossingsPerYear" to fastTrackCrossingsPerYear,
        "fastTrackMinutesSavedPerCrossing" to fastTrackMinutesSavedPerCrossing,
        "apecOtherTravelSavingsRub" to apecOtherTravelSavingsRub,
        "workHoursPerDay" to workHoursPerDay,
    )

    numericInputs.forEach { (name, value) ->
        requireFinite(name, value)
    }

    requirePositive("years", years)
    requirePositive("workDaysPerMonth", workDaysPerMonth)
    requirePositive("workHoursPerDay", workHoursPerDay)
    requirePositive("vndPerThousandRub", vndPerThousandRub)
    requirePositive("daysPerYear", daysPerYear)

    listOf(
        noEVisaPeriodDays,
        noEVisaChangedPeriodDays,
        eVisaPeriodDays,
        apecPeriodDays,
    ).forEach { period ->
        if (period <= 0.0) errors += "stay periods must be greater than 0"
    }

    if (
        bufferDaysBeforeExit >= noEVisaPeriodDays ||
        bufferDaysBeforeExit >= noEVisaChangedPeriodDays ||
        bufferDaysBeforeExit >= eVisaPeriodDays ||
        bufferDaysBeforeExit >= apecPeriodDays
    ) {
        errors += "bufferDaysBeforeExit must be lower than every stay period"
    }

    listOf(
        noEVisaRiskProbabilityPercent,
        eVisaRiskProbabilityPercent,
        apecRiskProbabilityPercent,
    ).forEach { probability ->
        if (probability !in 0.0..100.0) errors += "risk probabilities must be between 0 and 100"
    }

    listOf(
        "apecCardCostRub" to apecCardCostRub,
        "taxAndOpportunityCostFactor" to taxAndOpportunityCostFactor,
        "noEVisaStampChangeAfterYears" to noEVisaStampChangeAfterYears,
        "noEVisaRunCostVnd" to noEVisaRunCostVnd,
        "eVisaRunCostVnd" to eVisaRunCostVnd,
        "apecRunCostVnd" to apecRunCostVnd,
        "noEVisaLodgingCostVndPerRun" to noEVisaLodgingCostVndPerRun,
        "eVisaLodgingCostVndPerRun" to eVisaLodgingCostVndPerRun,
        "apecLodgingCostVndPerRun" to apecLodgingCostVndPerRun,
        "noEVisaLostWorkDaysPerRun" to noEVisaLostWorkDaysPerRun,
        "eVisaLostWorkDaysPerRun" to eVisaLostWorkDaysPerRun,
        "apecLostWorkDaysPerRun" to apecLostWorkDaysPerRun,
        "noEVisaExtraRiskDaysPerRun" to noEVisaExtraRiskDaysPerRun,
        "eVisaExtraRiskDaysPerRun" to eVisaExtraRiskDaysPerRun,
        "apecExtraRiskDaysPerRun" to apecExtraRiskDaysPerRun,
        "noEVisaAdminHoursPerRun" to noEVisaAdminHoursPerRun,
        "eVisaAdminHoursPerRun" to eVisaAdminHoursPerRun,
        "apecAdminHoursPerRun" to apecAdminHoursPerRun,
        "noEVisaDiscomfortRubPerRun" to noEVisaDiscomfortRubPerRun,
        "eVisaDiscomfortRubPerRun" to eVisaDiscomfortRubPerRun,
        "apecDiscomfortRubPerRun" to apecDiscomfortRubPerRun,
        "noEVisaRiskEventCostRub" to noEVisaRiskEventCostRub,
        "eVisaRiskEventCostRub" to eVisaRiskEventCostRub,
        "apecRiskEventCostRub" to apecRiskEventCostRub,
        "fastTrackCrossingsPerYear" to fastTrackCrossingsPerYear,
        "fastTrackMinutesSavedPerCrossing" to fastTrackMinutesSavedPerCrossing,
        "apecOtherTravelSavingsRub" to apecOtherTravelSavingsRub,
        "bufferDaysBeforeExit" to bufferDaysBeforeExit,
    ).forEach { (name, value) ->
        requireNonNegative(name, value)
    }

    require(errors.isEmpty()) { errors.distinct().joinToString("; ") }
}

fun calculateComparison(input: CalculatorInput): ComparisonResult {
    input.validateForCalculation()

    val scenarios = input.scenarioConfigs().map { config ->
        calculateScenario(input = input, config = config)
    }
    val ranking = scenarios.sortedBy { it.totalRub }
    val eVisa = scenarios.single { it.id == ScenarioId.E_VISA }
    val apec = scenarios.single { it.id == ScenarioId.APEC }
    val hourlyIncomeRub = input.monthlyIncomeRub / input.workDaysPerMonth / input.workHoursPerDay
    val breakEvenHours = if (apec.totalRub > eVisa.totalRub && eVisa.runsForPeriod > 0 && hourlyIncomeRub > 0) {
        (apec.totalRub - eVisa.totalRub) / eVisa.runsForPeriod / hourlyIncomeRub
    } else {
        0.0
    }

    return ComparisonResult(
        input = input,
        scenarios = scenarios,
        ranking = ranking,
        apecBreakEvenEVisaAdminHours = breakEvenHours,
    )
}

private fun calculateScenario(
    input: CalculatorInput,
    config: ScenarioConfig,
): ScenarioResult {
    val firstPhaseYears = config.changeAfterYears.coerceIn(0.0, input.years)
    val secondPhaseYears = (input.years - firstPhaseYears).coerceAtLeast(0.0)
    val effectivePeriodDays = (config.periodDays - input.bufferDaysBeforeExit).coerceAtLeast(1.0)
    val effectiveChangedPeriodDays = (config.changedPeriodDays - input.bufferDaysBeforeExit).coerceAtLeast(1.0)
    val initialRunsPerYear = input.daysPerYear / effectivePeriodDays
    val changedRunsPerYear = input.daysPerYear / effectiveChangedPeriodDays
    val runsForPeriod = initialRunsPerYear * firstPhaseYears + changedRunsPerYear * secondPhaseYears
    val runsPerYear = runsForPeriod / input.years
    val runCostRub = config.runCostVnd / (input.vndPerThousandRub / 1_000.0)
    val lodgingCostRub = config.lodgingCostVnd / (input.vndPerThousandRub / 1_000.0)
    val runAndLodgingCostRub = runCostRub + lodgingCostRub
    val runInflationMultiplier = phasedInflationMultiplier(
        firstPhaseYears = firstPhaseYears,
        secondPhaseYears = secondPhaseYears,
        initialRunsPerYear = initialRunsPerYear,
        changedRunsPerYear = changedRunsPerYear,
        annualInflationPercent = input.annualInflationPercent,
        exchangeRateChangePercent = input.exchangeRateChangePercent,
    )
    val travelCostRub = runAndLodgingCostRub * runInflationMultiplier
    val dailyIncomeRub = input.monthlyIncomeRub / input.workDaysPerMonth
    val incomeMultiplier = phasedInflationMultiplier(
        firstPhaseYears = firstPhaseYears,
        secondPhaseYears = secondPhaseYears,
        initialRunsPerYear = initialRunsPerYear,
        changedRunsPerYear = changedRunsPerYear,
        annualInflationPercent = input.incomeGrowthPercent,
        exchangeRateChangePercent = 0.0,
    )
    val lostWorkDays = runsForPeriod * config.lostWorkDaysPerRun
    val incomeLossRub = dailyIncomeRub * config.lostWorkDaysPerRun * incomeMultiplier * input.taxAndOpportunityCostFactor
    val extraRiskDaysCostRub = dailyIncomeRub * config.extraRiskDaysPerRun * incomeMultiplier * input.taxAndOpportunityCostFactor
    val adminTimeCostRub = dailyIncomeRub / input.workHoursPerDay * config.adminHoursPerRun * incomeMultiplier * input.taxAndOpportunityCostFactor
    val expectedRiskCostRub = runsForPeriod * (config.riskProbabilityPercent / 100.0) * config.riskEventCostRub
    val discomfortCostRub = runsForPeriod * config.discomfortRubPerRun
    val savingsRub = config.fastTrackSavingsRub + config.otherTravelSavingsRub

    return ScenarioResult(
        id = config.id,
        title = config.title,
        periodDays = config.periodDays,
        runCostVnd = config.runCostVnd,
        lostWorkDaysPerRun = config.lostWorkDaysPerRun,
        adminHoursPerRun = config.adminHoursPerRun,
        lodgingCostRub = lodgingCostRub * runInflationMultiplier,
        adminTimeCostRub = adminTimeCostRub,
        extraRiskDaysCostRub = extraRiskDaysCostRub,
        expectedRiskCostRub = expectedRiskCostRub,
        discomfortCostRub = discomfortCostRub,
        fastTrackSavingsRub = config.fastTrackSavingsRub,
        otherTravelSavingsRub = config.otherTravelSavingsRub,
        cardCostRub = config.cardCostRub,
        runsPerYear = runsPerYear,
        runsForPeriod = runsForPeriod,
        travelCostRub = travelCostRub,
        lostWorkDays = lostWorkDays,
        incomeLossRub = incomeLossRub,
        totalRub = travelCostRub + incomeLossRub + extraRiskDaysCostRub + adminTimeCostRub +
            expectedRiskCostRub + discomfortCostRub + config.cardCostRub - savingsRub,
    )
}

fun inflationMultiplier(years: Double, annualInflationPercent: Double): Double {
    val fullYears = years.toInt()
    val rate = 1.0 + annualInflationPercent / 100.0
    var multiplier = 0.0
    var currentYearWeight = 1.0

    repeat(fullYears) {
        multiplier += currentYearWeight
        currentYearWeight *= rate
    }

    val partialYear = years - fullYears
    if (partialYear > 0.0) {
        multiplier += currentYearWeight * partialYear
    }

    return multiplier
}

private fun phasedInflationMultiplier(
    firstPhaseYears: Double,
    secondPhaseYears: Double,
    initialRunsPerYear: Double,
    changedRunsPerYear: Double,
    annualInflationPercent: Double,
    exchangeRateChangePercent: Double,
): Double {
    return initialRunsPerYear * inflationMultiplier(firstPhaseYears, annualInflationPercent) +
        changedRunsPerYear * inflationMultiplier(secondPhaseYears, annualInflationPercent + exchangeRateChangePercent, startYear = firstPhaseYears)
}

private fun fastTrackSavings(input: CalculatorInput): Double {
    val hourlyIncomeRub = input.monthlyIncomeRub / input.workDaysPerMonth / input.workHoursPerDay
    val hoursSaved = input.fastTrackCrossingsPerYear * input.years * input.fastTrackMinutesSavedPerCrossing / 60.0
    return hoursSaved * hourlyIncomeRub * input.taxAndOpportunityCostFactor
}

fun inflationMultiplier(years: Double, annualInflationPercent: Double, startYear: Double): Double {
    val fullYears = years.toInt()
    val rate = 1.0 + annualInflationPercent / 100.0
    var multiplier = 0.0
    var currentYearWeight = pow(rate, startYear.toInt())

    repeat(fullYears) {
        multiplier += currentYearWeight
        currentYearWeight *= rate
    }

    val partialYear = years - fullYears
    if (partialYear > 0.0) {
        multiplier += currentYearWeight * partialYear
    }

    return multiplier
}

private fun pow(base: Double, exponent: Int): Double {
    var result = 1.0
    repeat(exponent.coerceAtLeast(0)) {
        result *= base
    }
    return result
}
